// Spec: specs/account/billing-info/functional_requirements.md §5, §9.5, §9.7
//       specs/payments/prepay/functional_requirements.md §6.5

// The provider's inline card fields. Renders inside whichever form is open; the confirm it
// registers is what the shared client's capture call reaches.

import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { useEffect, useMemo, useRef } from 'react'

import UUIButton from '@aytm/lib/uui/components/UUIButton'

import { uuiAppearance, uuiFonts } from '../lib/stripeAppearance'
import { CardCaptureBinding, CardCaptureResult } from '../types'

const GENERIC_CAPTURE_ERROR = 'We could not save your card. Please try again.'

// A reason the cardholder can act on is shown as given; anything else would misattribute a
// provider-side outage to their card.
const ACTIONABLE_ERROR_TYPES = ['card_error', 'validation_error']

type Props = {
  capture: CardCaptureBinding
  testId?: string
}

const CardFieldsBinder = ({ capture }: { capture: CardCaptureBinding }) => {
  const client = useStripe()
  const elements = useElements()
  const options = useMemo(
    () => ({ layout: 'tabs' as const, defaultValues: { billingDetails: { email: capture.email } } }),
    [capture.email]
  )

  useEffect(() => {
    if (!client || !elements) return

    return capture.register(async (): Promise<CardCaptureResult> => {
      const { error, setupIntent } = await client.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required'
      })

      if (error) {
        const actionable = ACTIONABLE_ERROR_TYPES.includes(error.type) && error.message
        // an actionable reason is bound to a field, and the provider prints it there itself
        if (actionable) return { ok: false, error: actionable, shownByProvider: true }

        return { ok: false, error: GENERIC_CAPTURE_ERROR }
      }

      const method = setupIntent?.payment_method
      const reference = typeof method === 'string' ? method : method?.id
      if (!reference) return { ok: false, error: GENERIC_CAPTURE_ERROR }

      return { ok: true, reference: { id: reference } }
    })
  }, [client, elements, capture])

  return <PaymentElement options={options} />
}

const CardEntryFields = ({ capture, testId = 'card-entry-fields' }: Props) => {
  const { clientPromise, clientSecret, sessionError, ensureSession } = capture

  // Once per flow-opening. Keyed on the callback instead, a session consumed by a successful
  // capture would mint a replacement while the form that consumed it is already closing.
  const ensureRef = useRef(ensureSession)
  ensureRef.current = ensureSession
  useEffect(() => {
    ensureRef.current()
  }, [])

  // A session that failed is not a session still coming: the fields will not arrive on their own,
  // so the wait ends and the cardholder is given the reason and a way to ask again (§5).
  if (sessionError) {
    return (
      <div className="tw:flex tw:flex-col tw:items-start tw:gap-y-2" data-testid={`${testId}-error`}>
        <div role="alert" className="tw:text-red-50 tw:text-xs tw:font-extralight">
          {sessionError}
        </div>
        <UUIButton
          uiSize="small"
          uiStyle="grey"
          type="button"
          className="tw:text-primary-text tw:bg-grey-800"
          onClick={ensureSession}
          data-testid={`${testId}-retry`}
        >
          Try again
        </UUIButton>
      </div>
    )
  }

  if (!clientPromise || !clientSecret) {
    return <div className="tw:min-h-[64px]" data-testid={`${testId}-pending`} aria-busy="true" />
  }

  return (
    <div data-testid={testId}>
      <Elements stripe={clientPromise} options={{ clientSecret, appearance: uuiAppearance(), fonts: uuiFonts }}>
        <CardFieldsBinder capture={capture} />
      </Elements>
    </div>
  )
}

export default CardEntryFields
